/**
 * Field / dispatch calendar helpers — avoid UTC calendar drift from `toISOString().slice(0, 10)`.
 */

/** Local calendar date YYYY-MM-DD (browser timezone). */
export function localDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/**
 * True when a YYYY-MM-DD date is before today's local calendar date — i.e. it
 * has expired. A date that is today is still good. Compares against the local
 * date, not the UTC one: in Central after 7 PM the UTC date is already tomorrow,
 * which flagged things due today as expired a few hours early (#1689).
 */
export function isPastDateKey(dateKey: string, now: Date = new Date()): boolean {
    return dateKey < localDateKey(now);
}

/** Add calendar days to a date key in local civil time (noon anchor avoids DST edge cases). */
export function addDaysToDateKey(dateKey: string, deltaDays: number): string {
    const [y, m, d] = dateKey.split("-").map((x) => parseInt(x, 10));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return dateKey;
    const dt = new Date(y, m - 1, d + deltaDays);
    return localDateKey(dt);
}

/** Weekday name ("Monday") for a YYYY-MM-DD date key, parsed as local civil time. */
export function weekdayFromDateKey(dateKey: string): string {
    const [y, m, d] = dateKey.split("-").map((x) => parseInt(x, 10));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return "";
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long" });
}

/**
 * When set (`VITE_FIELD_TIMEZONE`), schedule/dispatch format wall times in this IANA zone.
 * Otherwise the browser's local timezone is used.
 */
export function appDisplayTimeZone(): string | undefined {
    const raw = import.meta.env.VITE_FIELD_TIMEZONE as string | undefined;
    const t = raw?.trim();
    return t || undefined;
}

/** The org's wall-clock zone. Every "what day is it" in the app is asked in this zone. */
export const ORG_TIME_ZONE = "America/Chicago";

/**
 * `YYYY-MM-DD` for the instant `d` on the org's calendar (America/Chicago, or
 * `VITE_FIELD_TIMEZONE` when set) — the same day the server keys on, whatever
 * zone the iPad or browser happens to be set to.
 *
 * Use this — never the UTC ISO date of `new Date()` — for "today". The
 * ISO string is the UTC date, which in Central is already tomorrow from 7 PM
 * (6 PM in winter): a work order closed out at 8 PM was signed with tomorrow's
 * date (#1692). `src/server/orgDateKeyGuard.test.ts` fails CI on a new one.
 */
export function orgDateKey(d: Date): string {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: appDisplayTimeZone() ?? ORG_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Today on the org's calendar, `YYYY-MM-DD`. See `orgDateKey`. */
export function orgTodayKey(now: Date = new Date()): string {
    return orgDateKey(now);
}

/** Format an ISO instant for dispatch UI (time only). */
export function formatFieldTime(iso: string | null | undefined): string {
    if (!iso) return "—";
    const tz = appDisplayTimeZone();
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
        ...(tz ? { timeZone: tz } : {}),
        hour: "numeric",
        minute: "2-digit",
    }).format(d);
}
